import { createContext, useContext } from 'react';
import { MODULE_KEYS, NO_ADMIN_BYPASS_MODULES } from './module-keys';

export interface EffectivePermission {
  moduleCode: string;
  moduleName: string;
  action: string;
  granted: boolean;
  source: string;
  sourceRoleId: string | null;
  manualOverride: boolean;
}

export interface PermissionContextValue {
  permissions: EffectivePermission[];
  loading: boolean;
  hasPermission: (moduleCode: string, action: string) => boolean;
  getPermission: (moduleCode: string, action: string) => EffectivePermission | undefined;
  isModuleEnabled: (moduleCode: string) => boolean;
  enabledModules: Set<string>;
  isSuperAdmin: boolean;
}

export const PermissionContext = createContext<PermissionContextValue>({
  permissions: [],
  loading: true,
  hasPermission: () => false,
  getPermission: () => undefined,
  isModuleEnabled: () => false,
  enabledModules: new Set(),
  isSuperAdmin: false,
});

export function usePermissions(): PermissionContextValue {
  return useContext(PermissionContext);
}

/** One module, or a list of which any one will do. */
export type ModuleRequirement = string | readonly string[];

/** The rule behind `useHasPermission`, for callers that already hold the context. */
export function canUse(
  context: Pick<PermissionContextValue, 'hasPermission' | 'isModuleEnabled' | 'isSuperAdmin'>,
  moduleCode: string,
  action: string
): boolean {
  // Module must be enabled for everyone, including SuperAdmins
  if (!context.isModuleEnabled(moduleCode)) return false;
  if (context.isSuperAdmin && !NO_ADMIN_BYPASS_MODULES.has(moduleCode)) return true;
  return context.hasPermission(moduleCode, action);
}

/** True when any of the modules grants the action (a list is "any of"). */
export function canUseAny(
  context: Pick<PermissionContextValue, 'hasPermission' | 'isModuleEnabled' | 'isSuperAdmin'>,
  modules: ModuleRequirement,
  action: string
): boolean {
  const list = typeof modules === 'string' ? [modules] : modules;
  return list.some(moduleCode => canUse(context, moduleCode, action));
}

export function useHasPermission(moduleCode: ModuleRequirement, action: string): boolean {
  return canUseAny(usePermissions(), moduleCode, action);
}

/**
 * Resending an SMS the customer was already sent: `contracts:resend_sms`, or the
 * grant that already resent that message before it existed (server ADR-0092).
 */
export function canResendSms(
  context: Pick<PermissionContextValue, 'hasPermission' | 'isModuleEnabled' | 'isSuperAdmin'>,
  moduleCode: string,
  action: string
): boolean {
  return (
    canUse(context, MODULE_KEYS.CONTRACTS, 'resend_sms') || canUse(context, moduleCode, action)
  );
}

export function useCanResendSms(moduleCode: string, action: string): boolean {
  return canResendSms(usePermissions(), moduleCode, action);
}
